import type { RoleFormParams } from "@/services/role/roleApi"
import { Button, Form, Input, Space, Switch } from "antd"
import type React from "react"
import { memo, useEffect } from "react"
import { DragModal } from "@/components/Modal"

interface RoleModalProps {
  open: boolean
  title: string
  loading: boolean
  initialValues?: any | null
  onOk: (values: RoleFormParams) => void
  onCancel: () => void
}

/**
 * Component Modal Thêm mới / Cập nhật vai trò (RoleModal)
 */
const RoleModal: React.FC<RoleModalProps> = memo(({ open, title, loading, initialValues, onOk, onCancel }) => {
  const [form] = Form.useForm()

  useEffect(() => {
    if (!open) {
      return
    }

    if (initialValues) {
      form.setFieldsValue({
        ...initialValues,
        status: initialValues.status === 1,
      })
    } else {
      form.resetFields()
      form.setFieldsValue({
        status: true,
      })
    }
  }, [open, initialValues, form])

  const handleOk = async () => {
    try {
      const values = await form.validateFields()
      const submitData: RoleFormParams = {
        ...values,
        status: values.status ? 1 : 0,
      }
      onOk(submitData)
    } catch (error: any) {
      const firstErrorField = error.errorFields?.[0]?.name
      if (firstErrorField) {
        form.scrollToField(firstErrorField)
      }
    }
  }

  const handleAfterClose = () => {
    form.resetFields()
  }

  return (
    <DragModal
      centered
      title={title}
      open={open}
      onCancel={onCancel}
      width={500}
      destroyOnHidden={true}
      afterClose={handleAfterClose}
      footer={
        <Space>
          <Button onClick={onCancel}>Hủy</Button>
          <Button type="primary" onClick={handleOk} loading={loading}>
            Xác nhận
          </Button>
        </Space>
      }
      mask={{ closable: false }}
    >
      <Form form={form} layout="horizontal" labelCol={{ span: 6 }} wrapperCol={{ span: 16 }}>
        <Form.Item
          name="name"
          label="Tên vai trò"
          rules={[
            { required: true, message: "Vui lòng nhập tên vai trò" },
            { max: 50, message: "Tên vai trò không được vượt quá 50 ký tự" },
          ]}
        >
          <Input autoComplete="off" placeholder="Nhập tên vai trò" />
        </Form.Item>

        <Form.Item
          name="description"
          label="Mô tả"
          rules={[{ max: 200, message: "Mô tả không được vượt quá 200 ký tự" }]}
        >
          <Input.TextArea placeholder="Nhập mô tả vai trò" autoSize={{ minRows: 2, maxRows: 4 }} />
        </Form.Item>

        <Form.Item name="status" label="Trạng thái" valuePropName="checked">
          <Switch checkedChildren="Hoạt động" unCheckedChildren="Khóa" />
        </Form.Item>
      </Form>
    </DragModal>
  )
})

RoleModal.displayName = "RoleModal"

export default RoleModal
